// src/pages/QuizVerwaltung.jsx
//
// Admin: Fragen der Quiz-Station(en) pflegen, Stations-PIN fuer das Tablet
// setzen, Versuche einsehen, zuruecksetzen oder abgebrochene Versuche werten.
// Die Spielzeiten und Regeln (Abzug, Reihenfolge) stehen an der Station selbst
// (Stationen -> Details), weil sie wie jede Stationseinstellung zur Wertung
// gehoeren.

import { useCallback, useEffect, useMemo, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { ArrowUp, ArrowDown, Trash2, Plus, Upload, Download, RefreshCw, RotateCcw, CheckCircle2, Save, Volume2 } from "lucide-react";
import { toast } from "react-toastify";
import Button from "../components/ui/Button";
import Modal from "../components/ui/Modal";
import { fetchWithAuth } from "../utils/fetchWithAuth";
import { leseDateiAlsText } from "../utils/parseTabular";
import { fragenAusTabelle, fragenAlsCsv } from "../utils/quizImport";
import { vorlesenTesten, vorlesetext, vorlesenVerfuegbar, tempoFuer } from "../utils/vorlesen";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const BUCHSTABEN = ["A", "B", "C", "D"];

// IDs vergibt sonst der Server; eine vorlaeufige ID haelt im Editor die
// Eingabefelder beim Verschieben an der richtigen Frage.
const neueId = () => "n" + Math.random().toString(36).slice(2, 10);
const neueWf = () => ({ id: neueId(), typ: "wf", text: "", antworten: ["Wahr", "Falsch"], richtig: 0 });
const neueMc = () => ({ id: neueId(), typ: "mc", text: "", antworten: ["", "", "", ""], richtig: 0 });

/** Fuer den Server: leere 4. Antwort weglassen (3 Antworten sind erlaubt). */
function zumSpeichern(f) {
  if (f.typ === "wf") return { id: f.id, typ: "wf", text: f.text, antworten: ["Wahr", "Falsch"], richtig: f.richtig };
  const antworten = f.antworten.map((a) => a.trim());
  const ohneLetzte = antworten.length === 4 && !antworten[3] ? antworten.slice(0, 3) : antworten;
  return { id: f.id, typ: "mc", text: f.text, antworten: ohneLetzte, richtig: f.richtig };
}

/** Fuer den Editor: immer vier Antwortfelder bei Mehrfachauswahl. */
const zumBearbeiten = (f) => {
  const mitId = { ...f, id: f.id || neueId() };
  return f.typ === "mc" ? { ...mitId, antworten: [...f.antworten, "", "", "", ""].slice(0, 4) } : mitId;
};

const zeit = (ms) => (ms ? new Date(ms).toLocaleTimeString("de-DE", { hour: "2-digit", minute: "2-digit" }) : "—");
const punkte = (p) => (p === undefined || p === null ? "—" : String(p).replace(".", ","));

export default function QuizVerwaltung() {
  const [params, setParams] = useSearchParams();
  const [stationen, setStationen] = useState(null);
  const [daten, setDaten] = useState(null);
  // Zwei getrennte Fragensaetze: Kinder bekommen andere Fragen als Erwachsene.
  const [saetze, setSaetze] = useState({ erwachsene: [], kinder: [] });
  const [satz, setSatz] = useState("erwachsene");
  const [geaendert, setGeaendert] = useState(false);
  const [pin, setPin] = useState("");
  const [speichert, setSpeichert] = useState(false);
  const [fehler, setFehler] = useState("");
  const [import_, setImport] = useState(null); // { text, vorschau }
  const [bestaetigen, setBestaetigen] = useState(null); // { titel, text, aktion }

  const nr = Number(params.get("station")) || null;

  // Der Editor arbeitet immer auf dem gerade gewaehlten Satz.
  const fragen = saetze[satz];
  const setFragen = useCallback(
    (fn) => setSaetze((alt) => ({ ...alt, [satz]: typeof fn === "function" ? fn(alt[satz]) : fn })),
    [satz]
  );
  const saetzeAus = (d) => ({
    erwachsene: (d.fragen || []).map(zumBearbeiten),
    kinder: (d.fragen_kinder || []).map(zumBearbeiten),
  });

  const stationenLaden = useCallback(async () => {
    try {
      const liste = await fetchWithAuth(`${BACKEND_URL}/quiz/stationen`, null);
      setStationen(liste);
      if (!nr && liste.length > 0) setParams({ station: String(liste[0].station_number) }, { replace: true });
    } catch (err) {
      setFehler(err.message);
    }
  }, [nr, setParams]);

  const laden = useCallback(async () => {
    if (!nr) return;
    setFehler("");
    try {
      const d = await fetchWithAuth(`${BACKEND_URL}/quiz/${nr}/verwaltung`, null);
      setDaten(d);
      setSaetze(saetzeAus(d));
      // Reine Kinderstation: gleich den Kindersatz zeigen.
      if (d.station?.allow_groups === "Kinder") setSatz("kinder");
      else if (d.station?.allow_groups === "Erwachsene") setSatz("erwachsene");
      setPin(d.pin || "");
      setGeaendert(false);
    } catch (err) {
      setFehler(err.message);
    }
  }, [nr]);

  useEffect(() => {
    stationenLaden();
  }, [stationenLaden]);
  useEffect(() => {
    laden();
  }, [laden]);

  // Ungespeicherte Fragen nicht beim Schliessen des Tabs verlieren.
  useEffect(() => {
    if (!geaendert) return undefined;
    const warnen = (e) => {
      e.preventDefault();
      e.returnValue = "";
    };
    window.addEventListener("beforeunload", warnen);
    return () => window.removeEventListener("beforeunload", warnen);
  }, [geaendert]);

  const aendern = (i, neu) => {
    setFragen((alt) => alt.map((f, j) => (j === i ? { ...f, ...neu } : f)));
    setGeaendert(true);
  };
  const verschieben = (i, um) => {
    setFragen((alt) => {
      const j = i + um;
      if (j < 0 || j >= alt.length) return alt;
      const neu = [...alt];
      [neu[i], neu[j]] = [neu[j], neu[i]];
      return neu;
    });
    setGeaendert(true);
  };
  const entfernen = (i) => {
    setFragen((alt) => alt.filter((_, j) => j !== i));
    setGeaendert(true);
  };
  const hinzufuegen = (f) => {
    setFragen((alt) => [...alt, f]);
    setGeaendert(true);
  };

  const speichern = async (body, erfolg) => {
    setSpeichert(true);
    setFehler("");
    try {
      const d = await fetchWithAuth(`${BACKEND_URL}/quiz/${nr}/verwaltung`, null, {
        method: "PUT",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(body),
      });
      if ("fragen" in body || "fragen_kinder" in body) {
        setSaetze(saetzeAus(d));
        setGeaendert(false);
      }
      if ("pin" in body) setPin(d.pin || "");
      toast.success(erfolg);
      stationenLaden();
      laden();
    } catch (err) {
      setFehler(err.message);
    } finally {
      setSpeichert(false);
    }
  };

  const fragenSpeichern = () => {
    const laufend = (daten?.versuche || []).filter((v) => v.status === "laeuft").length;
    const gespielt = (daten?.versuche || []).length;
    const los = () =>
      speichern(
        { fragen: saetze.erwachsene.map(zumSpeichern), fragen_kinder: saetze.kinder.map(zumSpeichern) },
        "Fragen gespeichert"
      );
    if (gespielt > 0) {
      setBestaetigen({
        titel: "Fragen ändern, obwohl schon gespielt wurde?",
        text:
          `${gespielt} Team(s) haben das Quiz schon begonnen${laufend ? `, ${laufend} davon gerade jetzt` : ""}. ` +
          "Bereits gewertete Ergebnisse bleiben, wie sie sind. Laufende Versuche werden mit den neuen Fragen " +
          "gewertet; gelöschte Fragen zählen dort nicht. Für faire Bedingungen nur Tippfehler korrigieren.",
        aktion: los,
      });
    } else los();
  };

  const versuchAktion = (v, art) => {
    if (art === "reset") {
      setBestaetigen({
        titel: `Versuch von ${v.team_name} zurücksetzen?`,
        text:
          "Der Versuch und das Quiz-Ergebnis werden gelöscht. Das Team kann danach noch einmal von vorn spielen.",
        aktion: async () => {
          try {
            await fetchWithAuth(`${BACKEND_URL}/quiz/${nr}/versuche/${encodeURIComponent(v.team_id)}`, null, { method: "DELETE" });
            toast.success("Versuch zurückgesetzt");
            laden();
          } catch (err) {
            setFehler(err.message);
          }
        },
      });
    } else {
      setBestaetigen({
        titel: `Versuch von ${v.team_name} werten?`,
        text: `Gewertet werden die ${v.beantwortet} gesicherten Antworten; offene Fragen zählen als nicht beantwortet.`,
        aktion: async () => {
          try {
            const r = await fetchWithAuth(`${BACKEND_URL}/quiz/${nr}/versuche/${encodeURIComponent(v.team_id)}/werten`, null, { method: "POST" });
            toast.success(`Gewertet: ${punkte(r.ergebnis?.punkte)} Punkte`);
            laden();
          } catch (err) {
            setFehler(err.message);
          }
        },
      });
    }
  };

  const importVorschau = (text) => setImport({ text, vorschau: fragenAusTabelle(text) });
  const importUebernehmen = (ersetzen) => {
    const neu = import_.vorschau.fragen.map(zumBearbeiten);
    setFragen((alt) => (ersetzen ? neu : [...alt, ...neu]));
    setGeaendert(true);
    setImport(null);
    toast.info(`${neu.length} Fragen übernommen – zum Abschluss „Fragen speichern“.`);
  };

  const exportieren = () => {
    const blob = new Blob(["﻿" + fragenAlsCsv(fragen.map(zumSpeichern))], { type: "text/csv;charset=utf-8" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `quiz-station-${nr}-${satz}.csv`;
    a.click();
    URL.revokeObjectURL(url);
  };

  const zaehler = useMemo(
    () => ({ wf: fragen.filter((f) => f.typ === "wf").length, mc: fragen.filter((f) => f.typ === "mc").length }),
    [fragen]
  );

  // Welche Saetze braucht die Station? (Erlaubt fuer: Alle/Erwachsene/Kinder)
  const gruppe = daten?.station?.allow_groups || "Alle";
  const brauchtSatz = { erwachsene: gruppe !== "Kinder", kinder: gruppe !== "Erwachsene" };
  const SATZ_NAME = { erwachsene: "Erwachsene", kinder: "Kinder" };

  // ---------------------------------------------------------------------
  if (stationen && stationen.length === 0) {
    return (
      <div className="p-4 text-gray-200 max-w-3xl">
        <h1 className="text-2xl font-bold mb-4">Quiz</h1>
        <p className="text-gray-400">
          Es gibt noch keine Quiz-Station. Lege unter „Stationen“ eine neue Station an und wähle als
          Stations-Typ „Quiz am Tablet“. Danach pflegst du hier die Fragen.
        </p>
      </div>
    );
  }

  const st = daten?.station;
  const versuche = daten?.versuche || [];

  return (
    <div className="p-4 text-gray-200 max-w-5xl space-y-6">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <h1 className="text-2xl font-bold">Quiz</h1>
        {stationen && stationen.length > 1 && (
          <select
            value={nr || ""}
            onChange={(e) => {
              if (geaendert && !window.confirm("Ungespeicherte Fragen verwerfen?")) return;
              setParams({ station: e.target.value });
            }}
            className="px-3 py-2 rounded bg-secondary-800 border border-secondary-700"
          >
            {stationen.map((s) => (
              <option key={s.station_number} value={s.station_number}>
                Station {s.station_number} – {s.station_name}
              </option>
            ))}
          </select>
        )}
      </div>

      {fehler && <div className="p-3 rounded bg-red-800 text-red-100">{fehler}</div>}

      {st && (
        <section className="rounded-xl bg-secondary-900 p-4 grid gap-4 md:grid-cols-2">
          <div>
            <div className="text-xs font-bold text-yellow-400">Station {st.station_number}</div>
            <div className="text-lg font-semibold">{st.station_name}</div>
            <ul className="mt-2 text-sm text-gray-400 space-y-1">
              <li>Spielzeit Erwachsene: {st.sekunden} s · Kinder: {st.sekunden_kinder} s</li>
              <li>
                Abzug für falsche Antworten: Erwachsene {st.abzug ? "ja" : "nein"} · Kinder{" "}
                {st.abzug_kinder ? "ja" : "nein"}
              </li>
              <li>Reihenfolge: {st.mischen ? "für jedes Team gemischt" : "für alle gleich"}</li>
              <li>Erlaubt für: {st.allow_groups}</li>
            </ul>
            <p className="mt-2 text-xs text-gray-500">Ändern unter Stationen → Details.</p>
          </div>
          <div>
            <label className="text-sm text-gray-400">Betreuer-PIN fürs Tablet (4–8 Ziffern)</label>
            <div className="flex gap-2 mt-1">
              <input
                value={pin}
                onChange={(e) => setPin(e.target.value.replace(/\D/g, "").slice(0, 8))}
                inputMode="numeric"
                placeholder="keine PIN"
                className="flex-1 px-3 py-2 rounded bg-secondary-800 border border-secondary-700 font-mono"
              />
              <Button onClick={() => speichern({ pin }, pin ? "PIN gespeichert" : "PIN entfernt")} disabled={speichert || pin === (daten?.pin || "")}>
                Speichern
              </Button>
            </div>
            <p className="mt-2 text-xs text-gray-500">
              Mit der PIN wählt der Betreuer am Tablet ein Team ohne Laufzettel aus und kommt in die
              Tablet-Einstellungen (Station, Abmelden). Ohne PIN geht nur der QR-Code. Nicht auf
              öffentliche Aushänge schreiben.
            </p>
          </div>
        </section>
      )}

      {/* ---------------- Fragen ---------------- */}
      <section className="space-y-3">
        {brauchtSatz.erwachsene && brauchtSatz.kinder && (
          <div className="flex gap-2 border-b border-secondary-700">
            {["erwachsene", "kinder"].map((k) => (
              <button
                key={k}
                onClick={() => setSatz(k)}
                className={`px-4 py-2 -mb-px border-b-2 font-semibold ${
                  satz === k ? "border-yellow-400 text-yellow-300" : "border-transparent text-gray-400 hover:text-white"
                }`}
              >
                Fragen {SATZ_NAME[k]} ({saetze[k].length})
              </button>
            ))}
          </div>
        )}
        {["erwachsene", "kinder"]
          .filter((k) => brauchtSatz[k] && daten && saetze[k].length === 0)
          .map((k) => (
            <div key={k} className="p-3 rounded bg-yellow-900/40 text-yellow-200 text-sm">
              Für {SATZ_NAME[k]} sind noch keine Fragen hinterlegt – {SATZ_NAME[k]}-Teams können das Quiz
              so nicht starten.
            </div>
          ))}
        {satz === "kinder" && (
          <p className="text-sm text-gray-400">
            Die Fragen werden den Kindern vorgelesen – kurz und einfach formulieren. Die Spielzeit für
            Kinder stellst du an der Station ein.
          </p>
        )}
        <div className="flex flex-wrap items-center justify-between gap-2">
          <h2 className="text-xl font-semibold">
            Fragen {SATZ_NAME[satz]} <span className="text-gray-400 text-base font-normal">({fragen.length}: {zaehler.wf} Wahr/Falsch, {zaehler.mc} Mehrfachauswahl)</span>
          </h2>
          <div className="flex flex-wrap gap-2">
            <Button variant="secondary" onClick={() => setImport({ text: "", vorschau: null })}><Upload size={16} className="mr-2" /> Importieren</Button>
            <Button variant="secondary" onClick={exportieren} disabled={fragen.length === 0}><Download size={16} className="mr-2" /> Als CSV</Button>
            <Button variant="secondary" onClick={laden} disabled={speichert}><RefreshCw size={16} /></Button>
          </div>
        </div>

        {fragen.length === 0 && (
          <p className="text-gray-400">Noch keine Fragen. Einzeln anlegen oder aus einer Tabelle importieren.</p>
        )}

        {fragen.map((f, i) => (
          <div key={f.id} className="rounded-xl bg-secondary-900 p-4 space-y-3">
            <div className="flex items-center gap-2">
              <span className="text-sm font-bold text-yellow-400 w-10">{i + 1}.</span>
              <select
                value={f.typ}
                onChange={(e) =>
                  aendern(i, e.target.value === "wf"
                    ? { typ: "wf", antworten: ["Wahr", "Falsch"], richtig: 0 }
                    : { typ: "mc", antworten: ["", "", "", ""], richtig: 0 })
                }
                className="px-2 py-1 rounded bg-secondary-800 border border-secondary-700 text-sm"
              >
                <option value="wf">Wahr / Falsch</option>
                <option value="mc">Mehrfachauswahl</option>
              </select>
              <div className="flex-1" />
              {/* Aussprache pruefen (Zahlen, Abkuerzungen, bairische Woerter) –
                  hier mit der Stimme dieses Geraets, am Tablet klingt es aehnlich. */}
              {vorlesenVerfuegbar() && (
                <button
                  onClick={() =>
                    vorlesenTesten((r) => !r.ok && toast.error(r.text, { toastId: "vorlesen" }), {
                      text: vorlesetext(zumSpeichern(f)),
                      tempo: tempoFuer(satz === "kinder" ? "Kinder" : "Erwachsene"),
                    })
                  }
                  disabled={!f.text.trim()}
                  className="p-2 rounded hover:bg-secondary-800 disabled:opacity-30"
                  aria-label="Vorlesen"
                  title="Vorlesen (Aussprache prüfen)"
                >
                  <Volume2 size={16} />
                </button>
              )}
              <button onClick={() => verschieben(i, -1)} disabled={i === 0} className="p-2 rounded hover:bg-secondary-800 disabled:opacity-30" aria-label="Nach oben"><ArrowUp size={16} /></button>
              <button onClick={() => verschieben(i, 1)} disabled={i === fragen.length - 1} className="p-2 rounded hover:bg-secondary-800 disabled:opacity-30" aria-label="Nach unten"><ArrowDown size={16} /></button>
              <button onClick={() => entfernen(i)} className="p-2 rounded text-red-400 hover:bg-secondary-800" aria-label="Löschen"><Trash2 size={16} /></button>
            </div>
            <textarea
              value={f.text}
              onChange={(e) => aendern(i, { text: e.target.value })}
              maxLength={300}
              rows={2}
              placeholder="Fragetext"
              className="w-full px-3 py-2 rounded bg-secondary-800 border border-secondary-700"
            />
            {f.typ === "wf" ? (
              <div className="flex gap-4">
                {["Wahr", "Falsch"].map((t, k) => (
                  <label key={t} className="flex items-center gap-2 cursor-pointer">
                    <input type="radio" checked={f.richtig === k} onChange={() => aendern(i, { richtig: k })} className="accent-yellow-400" />
                    <span>{t} ist richtig</span>
                  </label>
                ))}
              </div>
            ) : (
              <div className="grid sm:grid-cols-2 gap-2">
                {f.antworten.map((a, k) => (
                  <label key={k} className="flex items-center gap-2">
                    <input
                      type="radio"
                      checked={f.richtig === k}
                      onChange={() => aendern(i, { richtig: k })}
                      className="accent-yellow-400"
                      title="Richtige Antwort"
                    />
                    <span className="w-5 text-gray-400">{BUCHSTABEN[k]}</span>
                    <input
                      value={a}
                      onChange={(e) => aendern(i, { antworten: f.antworten.map((x, m) => (m === k ? e.target.value : x)) })}
                      maxLength={120}
                      placeholder={k === 3 ? "Antwort D (optional)" : `Antwort ${BUCHSTABEN[k]}`}
                      className="flex-1 px-3 py-1.5 rounded bg-secondary-800 border border-secondary-700"
                    />
                  </label>
                ))}
              </div>
            )}
          </div>
        ))}

        <div className="flex flex-wrap gap-2 items-center sticky bottom-0 bg-secondary-950/95 py-3">
          <Button variant="secondary" onClick={() => hinzufuegen(neueWf())}><Plus size={16} className="mr-2" /> Wahr/Falsch-Frage</Button>
          <Button variant="secondary" onClick={() => hinzufuegen(neueMc())}><Plus size={16} className="mr-2" /> Frage mit Antworten</Button>
          <div className="flex-1" />
          {geaendert && <span className="text-sm text-yellow-300">Nicht gespeichert</span>}
          <Button onClick={fragenSpeichern} disabled={!geaendert || speichert}><Save size={16} className="mr-2" /> Fragen speichern</Button>
        </div>
      </section>

      {/* ---------------- Versuche ---------------- */}
      <section className="space-y-3">
        <h2 className="text-xl font-semibold">
          Versuche <span className="text-gray-400 text-base font-normal">({versuche.length})</span>
        </h2>
        {versuche.length === 0 ? (
          <p className="text-gray-400">Noch hat kein Team gespielt.</p>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="text-left text-gray-400 border-b border-secondary-700">
                <tr>
                  <th className="py-2 pr-4">Team</th>
                  <th className="py-2 pr-4">Start</th>
                  <th className="py-2 pr-4">Status</th>
                  <th className="py-2 pr-4">Beantwortet</th>
                  <th className="py-2 pr-4">Ergebnis</th>
                  <th className="py-2" />
                </tr>
              </thead>
              <tbody>
                {versuche.map((v) => (
                  <tr key={v.id} className="border-b border-secondary-800">
                    <td className="py-2 pr-4">
                      {v.team_name} <span className="text-gray-500">({v.kategorie})</span>
                    </td>
                    <td className="py-2 pr-4">{zeit(v.gestartet_ms)}</td>
                    <td className="py-2 pr-4">
                      {v.status === "fertig" ? (
                        <span className="text-green-400">fertig{v.verspaetet ? " (spät übertragen)" : ""}{v.gewertet_von ? " · von Orga gewertet" : ""}</span>
                      ) : v.abgelaufen ? (
                        <span className="text-red-400">abgebrochen</span>
                      ) : (
                        <span className="text-yellow-300">läuft</span>
                      )}
                    </td>
                    <td className="py-2 pr-4">{v.beantwortet} / {v.fragen}</td>
                    <td className="py-2 pr-4">
                      {v.ergebnis ? `${punkte(v.ergebnis.punkte)} P. (${v.ergebnis.richtig} r / ${v.ergebnis.falsch} f)` : "—"}
                    </td>
                    <td className="py-2 text-right whitespace-nowrap">
                      {v.status === "laeuft" && v.abgelaufen && (
                        <Button variant="secondary" className="!px-2 !py-1 text-xs mr-2" onClick={() => versuchAktion(v, "werten")}>
                          <CheckCircle2 size={14} className="mr-1" /> Werten
                        </Button>
                      )}
                      <Button variant="ghost" className="!px-2 !py-1 text-xs" onClick={() => versuchAktion(v, "reset")}>
                        <RotateCcw size={14} className="mr-1" /> Zurücksetzen
                      </Button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </section>

      {/* ---------------- Import ---------------- */}
      <Modal
        open={!!import_}
        onClose={() => setImport(null)}
        title={`Fragen für ${SATZ_NAME[satz]} importieren`}
        className="max-w-2xl"
        footer={
          <>
            <Button variant="secondary" onClick={() => setImport(null)}>Abbrechen</Button>
            <Button variant="secondary" disabled={!import_?.vorschau?.fragen.length} onClick={() => importUebernehmen(false)}>Anhängen</Button>
            <Button disabled={!import_?.vorschau?.fragen.length} onClick={() => importUebernehmen(true)}>Alle ersetzen</Button>
          </>
        }
      >
        <p className="text-sm text-gray-400">
          Tabelle mit Überschriftszeile – am einfachsten direkt aus Excel kopieren und hier einfügen.
          Erkannt werden die Spalten <b>Frage</b> (oder <i>Behauptung</i>), <b>Lösung</b> (oder{" "}
          <i>Richtig</i>) und für Mehrfachauswahl <b>Antwort A</b> bis <b>Antwort D</b>; weitere Spalten
          wie Nr. oder Themengebiet werden ignoriert. Wahr/Falsch: Lösung <i>wahr</i> oder <i>falsch</i>.
          Mehrfachauswahl: Lösung = Buchstabe der richtigen Antwort.
        </p>
        <input
          type="file"
          accept=".csv,.txt,text/csv"
          onChange={async (e) => {
            const datei = e.target.files?.[0];
            if (datei) importVorschau(await leseDateiAlsText(datei));
          }}
          className="text-sm"
        />
        <textarea
          value={import_?.text || ""}
          onChange={(e) => importVorschau(e.target.value)}
          rows={8}
          placeholder={"Frage;Richtig;A;B;C;D\nDer Maibaum ist aus Fichte.;wahr\nWie viele Liter hat eine Maß?;A;1;0,5;2"}
          className="w-full px-3 py-2 rounded bg-secondary-800 border border-secondary-700 font-mono text-sm"
        />
        {import_?.vorschau && (
          <div className="text-sm space-y-1">
            <div className="text-green-400">{import_.vorschau.fragen.length} Fragen erkannt.</div>
            {import_.vorschau.fehler.map((f) => (
              <div key={f} className="text-red-300">{f}</div>
            ))}
          </div>
        )}
      </Modal>

      <Modal
        open={!!bestaetigen}
        onClose={() => setBestaetigen(null)}
        title={bestaetigen?.titel}
        footer={
          <>
            <Button variant="secondary" onClick={() => setBestaetigen(null)}>Abbrechen</Button>
            <Button
              variant="danger"
              onClick={() => {
                const a = bestaetigen.aktion;
                setBestaetigen(null);
                a();
              }}
            >
              Ja, fortfahren
            </Button>
          </>
        }
      >
        <p className="text-gray-300">{bestaetigen?.text}</p>
      </Modal>
    </div>
  );
}
